import {
  createInviteLink,
  destroyInviteLinks,
  destroySubAccount,
  getSubAccounts,
  updateSubAccount,
} from '@/lib/api/subAccountApi'
import { GetSubAccountsQuery, UserInviteLink, UserRole, UserStatus } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { RootState } from '../store'

export type SubAccount = NonNullable<
  NonNullable<NonNullable<GetSubAccountsQuery['currentBillingAccount']>['users']['nodes']>[number]
>

const subAccountAdapter = createEntityAdapter<SubAccount, string>({
  selectId: subAccount => subAccount.id,
})

type UIState = {
  loaded: boolean
  totalCount: number
  hasNextPage: boolean
  endCursor: string | null
  isLoadingMore: boolean
}

const initialState: UIState & { inviteLinks: UserInviteLink[] } = {
  loaded: false,
  totalCount: 0,
  hasNextPage: false,
  endCursor: null,
  isLoadingMore: false,
  inviteLinks: [],
}

const subAccountSlice = createSlice({
  name: 'subAccounts',
  initialState: subAccountAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getSubAccountsThunk.fulfilled, (state, action) => {
        subAccountAdapter.setAll(state, action.payload.users.nodes?.map(node => node as SubAccount) || [])
        state.totalCount = action.payload.users.totalCount
        state.hasNextPage = action.payload.users.pageInfo?.hasNextPage || false
        state.endCursor = action.payload.users.pageInfo?.endCursor || null
        state.inviteLinks = action.payload.userInviteLinks || []
        state.loaded = true
      })
      .addCase(loadMoreSubAccountsThunk.pending, state => {
        state.isLoadingMore = true
      })
      .addCase(loadMoreSubAccountsThunk.fulfilled, (state, action) => {
        subAccountAdapter.addMany(state, action.payload.users.nodes?.map(node => node as SubAccount) || [])
        state.totalCount = action.payload.users.totalCount
        state.hasNextPage = action.payload.users.pageInfo?.hasNextPage || false
        state.endCursor = action.payload.users.pageInfo?.endCursor || null
        state.isLoadingMore = false
      })
      .addCase(loadMoreSubAccountsThunk.rejected, state => {
        state.isLoadingMore = false
      })
      .addCase(updateSubAccountThunk.fulfilled, (state, action) => {
        subAccountAdapter.updateOne(state, { id: action.payload.id, changes: action.payload })
      })
      .addCase(destroySubAccountThunk.fulfilled, (state, action) => {
        subAccountAdapter.removeOne(state, action.meta.arg.id)
      })
      .addCase(createInviteLinkThunk.fulfilled, (state, action) => {
        state.inviteLinks = [...state.inviteLinks, action.payload]
      })
      .addCase(destroyInviteLinksThunk.fulfilled, state => {
        state.inviteLinks = []
      })
  },
})

export const getSubAccountsThunk = createAsyncThunk<
  NonNullable<GetSubAccountsQuery['currentBillingAccount']>,
  { keyword?: string; includeInviteLinks?: boolean }
>('subAccounts/fetchSubAccounts', async (payload, thunkAPI) => {
  const { data } = await getSubAccounts({
    first: 50,
    keyword: payload.keyword,
    includeInviteLinks: payload.includeInviteLinks ?? true,
  })
  return data.currentBillingAccount || thunkAPI.rejectWithValue('Failed to fetch sub accounts')
})

export const loadMoreSubAccountsThunk = createAsyncThunk<
  NonNullable<GetSubAccountsQuery['currentBillingAccount']>,
  { keyword?: string }
>('subAccounts/loadMoreSubAccounts', async (payload, thunkAPI) => {
  const state = thunkAPI.getState() as RootState
  const { endCursor } = state.subAccounts

  if (!endCursor) {
    return thunkAPI.rejectWithValue('No cursor available for loading more')
  }

  const { data } = await getSubAccounts({
    first: 50,
    after: endCursor,
    keyword: payload.keyword,
    includeInviteLinks: false,
  })
  return data.currentBillingAccount || thunkAPI.rejectWithValue('Failed to load more sub accounts')
})

type UpdateSubAccountPayload = { id: string; role?: UserRole; status?: UserStatus; name?: string }
export const updateSubAccountThunk = createAsyncThunk<UpdateSubAccountPayload, UpdateSubAccountPayload>(
  'subAccounts/updateSubAccount',
  async (payload, thunkAPI) => {
    const { data } = await updateSubAccount(payload)
    return data?.updateBillingAccountUser?.success
      ? payload
      : thunkAPI.rejectWithValue(data?.updateBillingAccountUser?.errors)
  }
)

export const destroySubAccountThunk = createAsyncThunk<void, { id: string }>(
  'subAccounts/destroySubAccount',
  async (payload, thunkAPI) => {
    const { data } = await destroySubAccount(payload)
    return data?.destroyBillingAccountUser?.success
      ? undefined
      : thunkAPI.rejectWithValue('Failed to destroy sub account')
  }
)

export const createInviteLinkThunk = createAsyncThunk<UserInviteLink, { role: UserRole }>(
  'subAccounts/createInviteLink',
  async (payload, thunkAPI) => {
    const { data } = await createInviteLink({ role: payload.role })
    return data?.createUserInviteLink
      ? data.createUserInviteLink
      : thunkAPI.rejectWithValue('Failed to create invite link')
  }
)

export const destroyInviteLinksThunk = createAsyncThunk<void, void>(
  'subAccounts/destroyInviteLinks',
  async (_, thunkAPI) => {
    const { data } = await destroyInviteLinks()
    return data?.destroyUserInviteLinks?.succeed
      ? undefined
      : thunkAPI.rejectWithValue('Failed to destroy invite links')
  }
)

export const {
  selectAll: selectAllSubAccounts,
  selectById: selectSubAccountById,
  selectIds: selectSubAccountIds,
} = subAccountAdapter.getSelectors((state: RootState) => state.subAccounts)

export const selectInviteLinks = (state: RootState) => state.subAccounts.inviteLinks

export default subAccountSlice.reducer
